import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Document_FieldsFragment,
  EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
  useDocument_UpdateDocumentTagMutation,
  useDocumentTag_GetAllDocumentTagsQuery,
} from 'queries';
import * as yup from 'yup';

import FormSelectField from 'common/form-select-field';
import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  content: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
  },
  title: {
    fontWeight: 700,
    fontSize: '24px',
    marginTop: '18px',
    marginBottom: '32px',
    alignSelf: 'center',
  },
  documentName: {
    marginTop: '18px',
    alignSelf: 'center',
  },
  selectTagsInput: {
    borderRadius: '8px',
    '& fieldset': {
      border: '1px solid #ECEEF1',
    },
  },
  selectTagsContainer: {
    marginTop: '32px',
  },
  saveButton: {
    borderRadius: '100px',
    maxHeight: '40px',
    marginTop: '40px',
    marginBottom: '20px',
    backgroundColor: '#5ED1F0',
    color: '#1C2541',
  },
}));

type Props = {
  portalId: string;
  document: Document_FieldsFragment;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  onEditComplete: () => void;
};

const schema = yup.object().shape({
  documentTagId: yup.string().required('Required'),
});

type EditDocumentTagFormValues = {
  documentTagId: string;
  documentId: string;
};

const DocumentEditModal = ({
  document,
  setShowModal,
  showModal,
  onEditComplete,
  portalId,
}: Props) => {
  const classes = useStyles();

  const { data: documentTagsData, loading: documentTagsDataLoading } =
    useDocumentTag_GetAllDocumentTagsQuery({
      onError: (error) => {
        showAlert({
          message: 'Error getting all document tags: ' + error.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<EditDocumentTagFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      documentTagId: document.documentTagId || '',
      documentId: document.id,
    },
  });

  const [updateDocumentDocumentTag] = useDocument_UpdateDocumentTagMutation({
    refetchQueries: [
      {
        query: EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
        variables: { portalId },
      },
    ],
    onCompleted: () => {
      showToast({
        message: 'Document updated successfully',
        severity: NotificationSeverity.SUCCESS,
      });
      reset();
      onEditComplete();
    },
    onError: (e) =>
      showAlert({
        message: `Error updating document: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const onSubmit = (values: EditDocumentTagFormValues) => {
    const { documentTagId, documentId } = values;

    updateDocumentDocumentTag({
      variables: {
        input: {
          documentTagId,
          documentId,
          portalId,
        },
      },
    });
  };

  if (!documentTagsData || documentTagsDataLoading) {
    return <Spinner />;
  }

  const documentTags = documentTagsData?.data || [];

  return (
    <Modal
      setShowModal={setShowModal}
      onExitClick={() => setShowModal(false)}
      showModal={showModal}
      showActions={false}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.content}>
            <Typography className={classes.title}>Edit document tag</Typography>
            <Typography className={classes.documentName}>{document.name}</Typography>

            <div className={classes.selectTagsContainer}>
              <Controller
                name="documentTagId"
                control={control}
                render={({ field: { ref: _, ...field } }) => (
                  <FormSelectField
                    {...field}
                    errorMessage={errors.documentTagId?.message}
                    options={documentTags.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    label="Tag"
                    placeholder="Select document type"
                    className={classes.selectTagsInput}
                  />
                )}
              />
            </div>

            <Button
              type="submit"
              variant="outlined"
              className={classes.saveButton}
              disabled={isSubmitting}
            >
              Save
            </Button>
          </div>
        </form>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
};

export default DocumentEditModal;
